<script setup lang="ts">
import { useApiFetch } from "~/composible/useApiFetch";
import {defineBreadcrumb, useDevice} from "#imports";

const route = useRoute();
const router = useRouter();
const runtimeConfig = useRuntimeConfig();

const { slug } = route.params;
const { isMobile } = useDevice();
const currentPage = ref(route.query.page ? parseInt(route.query.page as string) : 1);

const { data, pending } = useApiFetch(`/genres/${slug}/books`, {
  query: { page: currentPage },
  watch: [currentPage]
});

const { data: genre, error, status} = await useApiFetch(`/genres/${slug}`, {
  transform: (genre) => genre.data
})

if (error.value?.statusCode === 404) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

const onChangePage = (page: number) => {
  router.push({ query: { page: page } });
  if (process.browser){
    nextTick(() => window.scrollTo({ top: 0 }))
  }
};

const crumbs = [
  { link: '/genres/all-novels', label: 'Novels' },
  { label: genre.value.name }
];

const headPaginationLinks = computed(() => {
  let links = [];
  if (currentPage.value > 1) {
    links.push({rel: 'prev', href: runtimeConfig.public.siteURL + route.path + '?page=' + (currentPage.value - 1)})
  }
  if (currentPage.value < data.value?.meta.last_page) {
    links.push({rel: 'next', href: runtimeConfig.public.siteURL + route.path + '?page=' + (currentPage.value + 1)})
  }

  return links;
});

useHead({
  link: headPaginationLinks
});

useSeoMeta({
  titleTemplate: '%s – Read on FictionMe',
  title: `${genre.value.name} Genre, Books and Novels` + (currentPage.value > 1 ? ` - Page ${currentPage.value}` : ``),
  description: `Discover the best books in ${genre.value.name} on FictionMe. Find bestsellers, new releases, and highly-rated books by leading authors`,
  ogTitle: `${genre.value.name} Genre, Books and Novels`,
  ogDescription: `Discover the best books in ${genre.value.name} on FictionMe. Find bestsellers, new releases, and highly-rated books by leading authors`,
  twitterTitle: `${genre.value.name} Genre, Books and Novels`,
  twitterDescription: `Discover the best books in ${genre.value.name} on FictionMe. Find bestsellers, new releases, and highly-rated books by leading authors`,
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      defineListItem({
        position: 1,
        name: 'Novels',
        item: '/genres/all-novels'
      }),
      defineListItem({
        position: 2,
        name: genre.value.name,
      }),
    ]
  })
]);
</script>

<template>
  <div class="container py-8 max-md:py-6">
    <div class="page-content flex flex-col gap-8">
      <breadcrumbs :breadcrumbs="crumbs" />

      <div class="flex items-start gap-5 max-lg:flex-col">
        <lazy-novels-menu :selected="genre.name" />

        <lazy-f-card :title="`${genre.name ?? ''} Novels & Books`" header-tag="h1">
          <div class="flex flex-col gap-10">
            <p v-if="currentPage === 1">
              <truncated-text
                  v-if="genre.description"
                  :text="genre.description"
                  :max="isMobile ? 150 : 500"
                  interactive
              />
            </p>

            <div v-loading="pending" v-if="data" class="grid gap-7 grid-cols-2 max-md:grid-cols-1">
              <book-detailed-card
                  v-for="book in data?.data"
                  :book="book"
                  :key="book.id"
              />
            </div>

            <pagination
                v-if="data?.meta"
                v-model="currentPage"
                :total-pages="data.meta.last_page"
                @changed="onChangePage"
            />
          </div>
        </lazy-f-card>
      </div>

      <cta-banner />
    </div>
  </div>
</template>

<style scoped lang="scss">
aside {
  box-shadow: 0px 1px 10px 0px rgba(34, 37, 49, 0.04), 0px 12px 80px 0px rgba(34, 37, 49, 0.04);
}
</style>